// Social-Inbox (Phase 2): Kommentare aller Kanäle lesen, beantworten, verbergen.
// Liest /api/marketing/inbox (Metricool-Aggregat), POST reply/hide.
// "User blockieren" gibt es in der Metricool-API nicht → Hinweis im UI; das
// bleibt eine manuelle Aktion in der nativen App (FB/IG/…).
import type { MarketingDeps } from "./marketing.js";
import { refreshMarketingTasks } from "./marketing.js";
import { NETS } from "./marketing-followers.js";

interface Comment {
  id: string; provider: string; status: string;
  author: string; text: string; postLink: string | null; createdAt: string | null;
  postText?: string | null; postMedia?: string | null; postMediaKind?: string | null;
  handled?: boolean; handledAction?: string | null;
}
interface InboxResponse { comments: Comment[] }

// Null-fest: `answered` kann wahr sein, ohne dass eine Antwort existiert (reine Sterne-Bewertung
// ohne Text gilt als erledigt). Ohne diesen Schutz warf esc(null) einen TypeError mitten im
// Aufbau der Liste — die Rezensionen blieben dann auf „Lade …" haengen (10.08.2026).
const esc = (s: string | null | undefined): string =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));

// Freundliche, marken-passende Emojis für Antworten (Wellness/Bildung).
const EMOJIS = ["😊", "🙂", "😍", "🤗", "🙏", "👍", "👏", "🙌", "❤️", "💚", "🌿", "✨", "💪", "🎉"];
const emojiBar = (): string =>
  `<div class="om-inbox-emojis">${EMOJIS.map((e) => `<button type="button" class="om-emoji" data-act="emoji" data-emoji="${e}" title="Emoji einfügen">${e}</button>`).join("")}</div>`;

function logoSvg(provider: string): string {
  const key = provider.replace("business", "");
  const meta = NETS[key];
  if (!meta) return "";
  return `<svg viewBox="0 0 24 24" width="20" height="20" fill="${meta.color}"><path d="${meta.path}"/></svg>`;
}

function netLabel(provider: string): string {
  return NETS[provider.replace("business", "")]?.label ?? provider;
}

// Zeichen-Limits der Netzwerke für Kommentar-Antworten. TikTok kappt bei 150 —
// längere Texte weist TikTok ab, Metricool meldet einen Fehler und wir sahen nur "Fehler (502)".
const REPLY_LIMITS: Record<string, number> = { tiktok: 150, youtube: 10000, instagram: 2200, facebook: 8000 };
function replyLimit(provider: string): number | null {
  return REPLY_LIMITS[provider.replace("business", "")] ?? null;
}
const providerLabel = netLabel;

// Rechte Spalte: Vorschau des Beitrags, auf den sich der Kommentar bezieht.
function preview(c: Comment): string {
  let media = "";
  if (c.postMediaKind === "image" && c.postMedia) {
    media = `<img class="om-inbox-postimg" src="${esc(c.postMedia)}" loading="lazy" alt="Beitrag">`;
  } else if (c.postMediaKind === "video" && c.postLink) {
    media = `<a class="om-inbox-postvideo" href="${esc(c.postLink)}" target="_blank" rel="noopener"><span>▶</span> Video / Reel ansehen</a>`;
  }
  const cap = c.postText ? `<div class="om-inbox-postcap">${esc(c.postText)}</div>` : "";
  const link = c.postLink ? `<a class="om-inbox-postlink" href="${esc(c.postLink)}" target="_blank" rel="noopener">Im Original öffnen ↗</a>` : "";
  if (!media && !cap && !link) return '<div class="om-inbox-preview"><span class="dim">keine Vorschau</span></div>';
  return `<div class="om-inbox-preview"><div class="om-inbox-preview-label">Beitrag</div>${media}${cap}${link}</div>`;
}

function card(c: Comment, formatDate: (s: string) => string): string {
  const isNew = c.status === "PENDING" && !c.handled;
  const badge = c.handled
    ? `<span class="om-inbox-badge done">✓ ${c.handledAction === "hidden" ? "verborgen" : "beantwortet"}</span>`
    : isNew ? '<span class="om-inbox-badge">neu</span>' : "";
  const when = c.createdAt ? formatDate(c.createdAt) : "";
  return `<div class="om-inbox-item${isNew ? " is-new" : ""}${c.handled ? " is-handled" : ""}" data-id="${esc(c.id)}" data-provider="${esc(c.provider)}">
    <div class="om-inbox-main">
      <div class="om-inbox-head"><span class="om-inbox-logo" title="${esc(netLabel(c.provider))}">${logoSvg(c.provider)}</span><b>${esc(c.author)}</b> ${badge}<span class="om-inbox-time">${esc(when)}</span></div>
      <div class="om-inbox-text">${esc(c.text) || '<i class="dim">(kein Text – evtl. Bild/Reaktion)</i>'}</div>
      <div class="om-inbox-actions">
        <button class="btn-link" data-act="reply">↩ Antworten</button>
        <button class="btn-link danger" data-act="hide">🚫 Verbergen</button>
      </div>
      <div class="om-inbox-reply" hidden>
        <textarea rows="2" placeholder="Antwort an ${esc(c.author)} …"></textarea>
        ${emojiBar()}
        <div class="om-inbox-reply-actions">
          <button class="btn-secondary om-ai-btn" data-act="suggest">✨ KI-Vorschlag</button>
          <button class="btn-primary" data-act="send">Senden</button>
          <button class="btn-secondary" data-act="cancel">Abbrechen</button>
          ${replyLimit(c.provider) ? `<span class="dim" data-reply-count>0/${replyLimit(c.provider)}</span>` : ""}
          <span class="inline-msg" data-reply-msg></span>
        </div>
      </div>
    </div>
    ${preview(c)}
  </div>`;
}

// ----- Direktnachrichten / Postfach -----
interface DmMessage { mine: boolean; text: string; hasAttachment: boolean; at: string | null }
interface Conversation {
  id: string; provider: string; status: string; partner: string;
  recipient: string; lastText: string; lastAt: string | null; messages: DmMessage[];
  answered?: boolean;
}
interface DmResponse { conversations: Conversation[] }

function insertEmoji(replyBox: HTMLElement, emoji: string): void {
  const ta = replyBox.querySelector("textarea") as HTMLTextAreaElement;
  const s = ta.selectionStart ?? ta.value.length, en = ta.selectionEnd ?? ta.value.length;
  ta.value = ta.value.slice(0, s) + emoji + ta.value.slice(en);
  const pos = s + emoji.length;
  ta.setSelectionRange(pos, pos);
  ta.focus();
}

function dmCard(c: Conversation, formatDate: (s: string) => string): string {
  const answered = !!c.answered;
  const unread = !answered && (c.status === "PENDING" || c.status === "UNREAD");
  const badge = answered ? '<span class="om-inbox-badge done">beantwortet</span>' : unread ? '<span class="om-inbox-badge">neu</span>' : "";
  const when = c.lastAt ? formatDate(c.lastAt) : "";
  const bubbles = c.messages.map((mm) => {
    const body = esc(mm.text) || (mm.hasAttachment ? '<i class="dim">📎 Anhang / geteilter Beitrag</i>' : '<i class="dim">—</i>');
    return `<div class="om-dm-msg ${mm.mine ? "mine" : "theirs"}">${body}</div>`;
  }).join("");
  return `<div class="om-dm-item${answered ? " is-handled" : unread ? " is-new" : ""}" data-id="${esc(c.id)}" data-provider="${esc(c.provider)}" data-recipient="${esc(c.recipient)}">
    <div class="om-dm-head"><span class="om-inbox-logo" title="${esc(netLabel(c.provider))}">${logoSvg(c.provider)}</span><b>${esc(c.partner)}</b> ${badge}<span class="om-inbox-time">${esc(when)}</span></div>
    <div class="om-dm-last">${esc(c.lastText) || '<i class="dim">(kein Text)</i>'}</div>
    <div class="om-dm-thread" hidden>${bubbles}</div>
    <div class="om-inbox-actions">
      <button class="btn-link" data-act="dm-open">💬 Verlauf &amp; Antworten</button>
      ${answered ? '<button class="btn-link" data-act="dm-undone">↩ Wieder offen</button>' : '<button class="btn-link" data-act="dm-done">✓ Erledigt</button>'}
    </div>
    <div class="om-inbox-reply" hidden>
      <textarea rows="2" placeholder="Nachricht an ${esc(c.partner)} …"></textarea>
      ${emojiBar()}
      <div class="om-inbox-reply-actions">
        <button class="btn-secondary om-ai-btn" data-act="dm-suggest">✨ KI-Vorschlag</button>
        <button class="btn-primary" data-act="dm-send">Senden</button>
        <button class="btn-secondary" data-act="dm-cancel">Schließen</button>
        <span class="inline-msg" data-reply-msg></span>
      </div>
    </div>
  </div>`;
}

// ----- Google-Rezensionen (GMB) -----
interface Review { id: string; reviewer: string; stars: number; message: string; reply: string | null; status: string; at: string | null; answered?: boolean }
interface ReviewResponse { reviews: Review[]; unavailable?: boolean }

function reviewCard(c: Review, formatDate: (s: string) => string): string {
  const hasReply = !!c.reply;
  const answered = !!c.answered;
  const badge = hasReply ? '<span class="om-inbox-badge done">beantwortet</span>' : answered ? '<span class="om-inbox-badge done">erledigt</span>' : '<span class="om-inbox-badge">offen</span>';
  const n = Math.max(0, Math.min(5, Math.round(c.stars)));
  const stars = "★".repeat(n) + "☆".repeat(5 - n);
  const when = c.at ? formatDate(c.at) : "";
  const existing = hasReply ? `<div class="om-review-existing"><b>Eure Antwort:</b> ${esc(c.reply)}</div>` : "";
  return `<div class="om-dm-item${answered ? " is-handled" : " is-new"}" data-id="${esc(c.id)}" data-reviewer="${esc(c.reviewer)}" data-stars="${c.stars}" data-message="${esc(c.message)}">
    <div class="om-dm-head"><span class="om-review-stars" title="${c.stars} Sterne">${stars}</span> <b>${esc(c.reviewer)}</b> ${badge}<span class="om-inbox-time">${esc(when)}</span></div>
    <div class="om-inbox-text">${esc(c.message) || '<i class="dim">(nur Sterne-Bewertung, kein Text)</i>'}</div>
    ${existing}
    <div class="om-inbox-actions">
      <button class="btn-link" data-act="rev-reply">↩ ${hasReply ? "Antwort ändern" : "Öffentlich antworten"}</button>
      ${answered ? '<button class="btn-link" data-act="rev-undone">↩ Wieder offen</button>' : '<button class="btn-link" data-act="rev-done">✓ Erledigt</button>'}
    </div>
    <div class="om-inbox-reply" hidden>
      <textarea rows="2" placeholder="Öffentliche Antwort an ${esc(c.reviewer)} …">${hasReply ? esc(c.reply) : ""}</textarea>
      ${emojiBar()}
      <div class="om-inbox-reply-actions">
        <button class="btn-secondary om-ai-btn" data-act="rev-suggest">✨ KI-Vorschlag</button>
        <button class="btn-primary" data-act="rev-send">Antwort posten</button>
        <button class="btn-secondary" data-act="rev-cancel">Abbrechen</button>
        <span class="inline-msg" data-reply-msg></span>
      </div>
    </div>
  </div>`;
}

export function initInboxTab(m: HTMLElement, deps: MarketingDeps): void {
  const host = m.querySelector("[data-inbox-list]") as HTMLElement | null;
  const stamp = m.querySelector("[data-inbox-stamp]") as HTMLElement | null;
  const refresh = m.querySelector('[data-action="refresh-inbox"]') as HTMLButtonElement | null;
  const dmHost = m.querySelector("[data-dm-list]") as HTMLElement | null;
  const reviewHost = m.querySelector("[data-review-list]") as HTMLElement | null;
  if (!host || !dmHost || !reviewHost) return;
  let mode = "comments";
  let dmsLoaded = false;
  let reviewsLoaded = false;

  const load = async (): Promise<void> => {
    host.innerHTML = '<p class="dim">Lade …</p>';
    const r = await deps.api<InboxResponse>("/api/marketing/inbox");
    const list = r.data?.comments ?? [];
    if (!list.length) {
      host.innerHTML = '<p class="dim">Keine Kommentare gefunden (oder Metricool-Berechtigungen je Netzwerk prüfen).</p>';
      if (stamp) stamp.textContent = "";
      return;
    }
    host.innerHTML = list.map((c) => card(c, deps.formatDate)).join("");
    // Abgelaufene/blockierte CDN-Bilder ausblenden (Vorschau bleibt mit Caption + Link nutzbar)
    host.querySelectorAll<HTMLImageElement>(".om-inbox-postimg").forEach((img) => {
      img.addEventListener("error", () => { img.style.display = "none"; });
    });
    const open = list.filter((c) => c.status === "PENDING" && !c.handled).length;
    if (stamp) stamp.textContent = `${list.length} Kommentare · ${open} offen`;
    const cBtn = m.querySelector('[data-inbox-mode="comments"]');
    if (cBtn) cBtn.textContent = open ? `Kommentare (${open})` : "Kommentare";
    void refreshMarketingTasks(deps, m);
  };

  const loadDMs = async (): Promise<void> => {
    dmHost.innerHTML = '<p class="dim">Lade …</p>';
    const r = await deps.api<DmResponse>("/api/marketing/inbox/conversations");
    const list = r.data?.conversations ?? [];
    if (!list.length) { dmHost.innerHTML = '<p class="dim">Keine Nachrichten gefunden.</p>'; if (stamp) stamp.textContent = ""; return; }
    dmHost.innerHTML = list.map((c) => dmCard(c, deps.formatDate)).join("");
    const open = list.filter((c) => !c.answered).length;
    if (stamp) stamp.textContent = `${list.length} Konversationen · ${open} offen`;
    const dmBtn = m.querySelector('[data-inbox-mode="dms"]');
    if (dmBtn) dmBtn.textContent = open ? `Nachrichten (${open})` : "Nachrichten";
    void refreshMarketingTasks(deps, m);
  };

  const loadReviews = async (): Promise<void> => {
    reviewHost.innerHTML = '<p class="dim">Lade …</p>';
    const r = await deps.api<ReviewResponse>("/api/marketing/inbox/reviews");
    // Fehler/Timeout: NICHT als „keine Rezensionen" tarnen, sondern Retry ermöglichen (Flag zurücksetzen,
    // damit ein erneutes Öffnen des Tabs bzw. „↻ Aktualisieren" neu lädt).
    if (r.status !== 200 || r.data?.unavailable) {
      reviewHost.innerHTML = '<p class="dim">Google-Rezensionen gerade nicht erreichbar (Metricool langsam/überlastet). Bitte „↻ Aktualisieren“ klicken.</p>';
      reviewsLoaded = false; if (stamp) stamp.textContent = "";
      return;
    }
    const list = r.data?.reviews ?? [];
    if (!list.length) { reviewHost.innerHTML = '<p class="dim">Keine Google-Rezensionen gefunden.</p>'; if (stamp) stamp.textContent = ""; return; }
    reviewHost.innerHTML = list.map((c) => reviewCard(c, deps.formatDate)).join("");
    const open = list.filter((c) => !c.answered).length;
    if (stamp) stamp.textContent = `${list.length} Rezensionen · ${open} offen`;
    const rBtn = m.querySelector('[data-inbox-mode="reviews"]');
    if (rBtn) rBtn.textContent = open ? `Bewertungen (${open})` : "Bewertungen";
    void refreshMarketingTasks(deps, m);
  };

  // Rezensions-Interaktionen
  reviewHost.addEventListener("click", async (ev) => {
    const btn = (ev.target as HTMLElement).closest("button[data-act]") as HTMLButtonElement | null;
    if (!btn) return;
    const item = btn.closest(".om-dm-item") as HTMLElement | null;
    if (!item) return;
    const id = item.dataset.id!;
    const replyBox = item.querySelector(".om-inbox-reply") as HTMLElement;
    const act = btn.dataset.act;
    if (act === "rev-reply") { replyBox.hidden = !replyBox.hidden; if (!replyBox.hidden) (replyBox.querySelector("textarea") as HTMLTextAreaElement).focus(); return; }
    if (act === "rev-cancel") { replyBox.hidden = true; return; }
    if (act === "rev-done" || act === "rev-undone") {
      btn.disabled = true;
      const r = await deps.api(`/api/marketing/inbox/${act === "rev-done" ? "done" : "undone"}`, "POST", { id });
      if (r.status === 200) { await loadReviews(); } else { btn.disabled = false; }
      return;
    }
    if (act === "emoji") { insertEmoji(replyBox, btn.dataset.emoji ?? ""); return; }
    if (act === "rev-suggest") {
      const ta = replyBox.querySelector("textarea") as HTMLTextAreaElement;
      const msg = replyBox.querySelector("[data-reply-msg]") as HTMLElement;
      const prev = btn.textContent;
      btn.disabled = true; btn.textContent = "✨ …"; msg.textContent = "KI formuliert …";
      const r = await deps.api<{ suggestion?: string }>("/api/marketing/inbox/reviews/suggest", "POST", {
        reviewer: item.dataset.reviewer, stars: Number(item.dataset.stars), message: item.dataset.message,
      });
      btn.disabled = false;
      if (r.status === 200 && r.data?.suggestion) { ta.value = r.data.suggestion; ta.focus(); btn.textContent = "🔄 Anderer Vorschlag"; msg.textContent = "Vorschlag eingefügt – anpassen & posten."; }
      else { btn.textContent = prev ?? "✨ KI-Vorschlag"; msg.textContent = `KI-Fehler (${r.status})`; }
      return;
    }
    if (act === "rev-send") {
      const ta = replyBox.querySelector("textarea") as HTMLTextAreaElement;
      const msg = replyBox.querySelector("[data-reply-msg]") as HTMLElement;
      const text = ta.value.trim();
      if (!text) { msg.textContent = "Bitte Text eingeben."; return; }
      btn.disabled = true; msg.textContent = "Sende …";
      const r = await deps.api("/api/marketing/inbox/reviews/reply", "POST", { reviewId: id, text });
      btn.disabled = false;
      if (r.status === 200) { msg.textContent = "✓ gepostet"; setTimeout(() => { void loadReviews(); }, 900); }
      else { msg.textContent = `Fehler (${r.status})`; }
      return;
    }
  });

  // DM-Interaktionen (Verlauf öffnen, Emoji, senden)
  dmHost.addEventListener("click", async (ev) => {
    const btn = (ev.target as HTMLElement).closest("button[data-act]") as HTMLButtonElement | null;
    if (!btn) return;
    const item = btn.closest(".om-dm-item") as HTMLElement | null;
    if (!item) return;
    const id = item.dataset.id!, provider = item.dataset.provider!, recipient = item.dataset.recipient!;
    const thread = item.querySelector(".om-dm-thread") as HTMLElement;
    const replyBox = item.querySelector(".om-inbox-reply") as HTMLElement;
    const act = btn.dataset.act;

    if (act === "dm-open") {
      const show = replyBox.hidden;
      thread.hidden = !show; replyBox.hidden = !show;
      if (show) { thread.scrollTop = thread.scrollHeight; (replyBox.querySelector("textarea") as HTMLTextAreaElement).focus(); }
      return;
    }
    if (act === "dm-cancel") { thread.hidden = true; replyBox.hidden = true; return; }
    if (act === "dm-done" || act === "dm-undone") {
      btn.disabled = true;
      const r = await deps.api(`/api/marketing/inbox/${act === "dm-done" ? "done" : "undone"}`, "POST", { id });
      if (r.status === 200) { await loadDMs(); } else { btn.disabled = false; }
      return;
    }
    if (act === "emoji") { insertEmoji(replyBox, btn.dataset.emoji ?? ""); return; }
    if (act === "dm-suggest") {
      const ta = replyBox.querySelector("textarea") as HTMLTextAreaElement;
      const msg = replyBox.querySelector("[data-reply-msg]") as HTMLElement;
      const partner = item.querySelector(".om-dm-head b")?.textContent ?? "";
      const messages = Array.from(item.querySelectorAll(".om-dm-msg")).map((el) => ({ mine: el.classList.contains("mine"), text: el.textContent?.trim() ?? "" }));
      const prev = btn.textContent;
      btn.disabled = true; btn.textContent = "✨ …"; msg.textContent = "KI liest den Verlauf …";
      const r = await deps.api<{ suggestion?: string }>("/api/marketing/inbox/conversations/suggest", "POST", { partner, messages });
      btn.disabled = false;
      if (r.status === 200 && r.data?.suggestion) { ta.value = r.data.suggestion; ta.focus(); btn.textContent = "🔄 Anderer Vorschlag"; msg.textContent = "Vorschlag eingefügt – anpassen & senden."; }
      else { btn.textContent = prev ?? "✨ KI-Vorschlag"; msg.textContent = `KI-Fehler (${r.status})`; }
      return;
    }
    if (act === "dm-send") {
      const ta = replyBox.querySelector("textarea") as HTMLTextAreaElement;
      const msg = replyBox.querySelector("[data-reply-msg]") as HTMLElement;
      const text = ta.value.trim();
      if (!text) { msg.textContent = "Bitte Text eingeben."; return; }
      if (!recipient) { msg.textContent = "Kein Empfänger ermittelbar."; return; }
      btn.disabled = true; msg.textContent = "Sende …";
      const r = await deps.api("/api/marketing/inbox/conversations/reply", "POST", { provider, conversationId: id, recipient, text });
      btn.disabled = false;
      if (r.status === 200) { msg.textContent = "✓ gesendet"; ta.value = ""; setTimeout(() => { void loadDMs(); }, 900); }
      else { msg.textContent = `Fehler (${r.status})`; }
      return;
    }
  });

  // Live-Zeichenzähler: zeigt beim Tippen, ob die Antwort ins Plattform-Limit passt (TikTok 150).
  host.addEventListener("input", (ev) => {
    const ta = (ev.target as HTMLElement).closest("textarea");
    if (!ta) return;
    const item = ta.closest(".om-inbox-item") as HTMLElement | null;
    const out = item?.querySelector("[data-reply-count]") as HTMLElement | null;
    if (!item || !out) return;
    const lim = replyLimit(item.dataset.provider ?? "");
    if (!lim) return;
    const n = (ta as HTMLTextAreaElement).value.trim().length;
    out.textContent = `${n}/${lim}`;
    out.classList.toggle("danger", n > lim);
  });

  host.addEventListener("click", async (ev) => {
    const btn = (ev.target as HTMLElement).closest("button[data-act]") as HTMLButtonElement | null;
    if (!btn) return;
    const item = btn.closest(".om-inbox-item") as HTMLElement | null;
    if (!item) return;
    const id = item.dataset.id!;
    const provider = item.dataset.provider!;
    const replyBox = item.querySelector(".om-inbox-reply") as HTMLElement;
    const act = btn.dataset.act;

    if (act === "reply") { replyBox.hidden = !replyBox.hidden; if (!replyBox.hidden) (replyBox.querySelector("textarea") as HTMLTextAreaElement).focus(); return; }
    if (act === "cancel") { replyBox.hidden = true; return; }
    if (act === "emoji") {
      const ta = replyBox.querySelector("textarea") as HTMLTextAreaElement;
      const e = btn.dataset.emoji ?? "";
      const s = ta.selectionStart ?? ta.value.length, en = ta.selectionEnd ?? ta.value.length;
      ta.value = ta.value.slice(0, s) + e + ta.value.slice(en);
      const pos = s + e.length;
      ta.setSelectionRange(pos, pos);
      ta.focus();
      return;
    }

    if (act === "suggest") {
      const ta = replyBox.querySelector("textarea") as HTMLTextAreaElement;
      const msg = replyBox.querySelector("[data-reply-msg]") as HTMLElement;
      const author = item.querySelector(".om-inbox-head b")?.textContent ?? "";
      const text = item.querySelector(".om-inbox-text")?.textContent ?? "";
      const postText = item.querySelector(".om-inbox-postcap")?.textContent ?? "";
      const prev = btn.textContent;
      btn.disabled = true; btn.textContent = "✨ …"; msg.textContent = "KI formuliert …";
      const r = await deps.api<{ suggestion?: string }>("/api/marketing/inbox/comments/suggest", "POST", { author, text, postText, provider });
      btn.disabled = false;
      if (r.status === 200 && r.data?.suggestion) {
        ta.value = r.data.suggestion; ta.focus(); btn.textContent = "🔄 Anderer Vorschlag"; msg.textContent = "Vorschlag eingefügt – anpassen & posten.";
        ta.dispatchEvent(new Event("input", { bubbles: true }));   // Zeichenzähler aktualisieren
      }
      else { btn.textContent = prev ?? "✨ KI-Vorschlag"; msg.textContent = `KI-Fehler (${r.status})`; }
      return;
    }
    if (act === "send") {
      const ta = replyBox.querySelector("textarea") as HTMLTextAreaElement;
      const msg = replyBox.querySelector("[data-reply-msg]") as HTMLElement;
      const text = ta.value.trim();
      if (!text) { msg.textContent = "Bitte Text eingeben."; return; }
      // Plattform-Limit vorab prüfen — sonst lehnt das Netzwerk ab und wir sehen nur einen 502.
      const lim = replyLimit(provider);
      if (lim && text.length > lim) {
        msg.textContent = `Zu lang für ${providerLabel(provider)}: ${text.length} von max. ${lim} Zeichen — bitte kürzen.`;
        return;
      }
      btn.disabled = true; msg.textContent = "Sende …";
      const r = await deps.api<{ error?: string; status?: number; detail?: string }>(
        "/api/marketing/inbox/reply", "POST", { provider, commentId: id, text });
      btn.disabled = false;
      if (r.status === 200) { msg.textContent = "✓ gesendet"; ta.value = ""; setTimeout(() => { void load(); }, 900); }
      else {
        // Klartext statt nur "Fehler (502)": Metricool/Netzwerk-Detail durchreichen.
        const d = (r.data?.detail ?? "").replace(/\s+/g, " ").trim();
        msg.textContent = d ? `Fehler (${r.status}): ${d.slice(0, 180)}` : `Fehler (${r.status})`;
      }
      return;
    }

    if (act === "hide") {
      if (!confirm("Diesen Kommentar wirklich verbergen/löschen? (Auf der Plattform entfernt)")) return;
      btn.disabled = true; btn.textContent = "…";
      const r = await deps.api("/api/marketing/inbox/hide", "POST", { provider, commentId: id });
      if (r.status === 200) { await load(); }
      else { btn.disabled = false; btn.textContent = "🚫 Verbergen"; alert(`Konnte nicht verbergen (Fehler ${r.status}).`); }
      return;
    }
  });

  const desc = m.querySelector("[data-inbox-desc]") as HTMLElement | null;
  const setMode = (newMode: string): void => {
    mode = newMode;
    m.querySelectorAll<HTMLButtonElement>('[data-inbox-mode]').forEach((b) => b.classList.toggle("active", b.dataset.inboxMode === newMode));
    host.hidden = newMode !== "comments";
    dmHost.hidden = newMode !== "dms";
    reviewHost.hidden = newMode !== "reviews";
    if (desc) desc.textContent =
      newMode === "dms" ? "Direktnachrichten (Instagram / Facebook Messenger) — Verlauf ansehen & antworten. Blockieren nur in der nativen App."
      : newMode === "reviews" ? "Google-Rezensionen (Business-Profil) — öffentlich antworten. Antworten verbessern lokale Reputation & Ranking."
      : "Kommentare aller Kanäle (FB · Instagram · YouTube · TikTok) — antworten oder verbergen.";
    if (newMode === "dms" && !dmsLoaded) { dmsLoaded = true; void loadDMs(); }
    if (newMode === "reviews" && !reviewsLoaded) { reviewsLoaded = true; void loadReviews(); }
  };
  m.querySelectorAll<HTMLButtonElement>('[data-inbox-mode]').forEach((b) => {
    b.addEventListener("click", () => setMode(b.dataset.inboxMode ?? "comments"));
  });

  refresh?.addEventListener("click", () => { if (mode === "comments") void load(); else if (mode === "dms") void loadDMs(); else void loadReviews(); });
  void load();
}
